import {
  AnalyticsVisualizationAxisScale as AxisScale,
  AnalyticsVisualizationGradientDirection as GradientDirection,
  AnalyticsVisualizationSortDirection as SortDirection,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {RegressionConfig} from 'ui-library/charting/statistics/regression/RegressionTypes';
import {Override} from 'util/Types';

import {AnalyticsVisualization} from './__generated/AnalyticsVisualization';

type AnalyticsVisualizationParameters = AnalyticsVisualization['parameters'];

type BarChartParameters = ExtractByTypename<
  AnalyticsVisualizationParameters,
  'AnalyticsVisualizationBarChartParametersNode'
>;

type HeatmapParameters = ExtractByTypename<
  AnalyticsVisualizationParameters,
  'AnalyticsVisualizationHeatmapParametersNode'
>;

type LineChartParameters = ExtractByTypename<
  AnalyticsVisualizationParameters,
  'AnalyticsVisualizationLineChartParametersNode'
>;

type OutlierConfig = NonNullable<
  ExtractByTypename<
    AnalyticsVisualizationParameters,
    'AnalyticsVisualizationScatterPlotParametersNode'
  >['outlierConfig']
>;

type PieChartParameters = ExtractByTypename<
  AnalyticsVisualizationParameters,
  'AnalyticsVisualizationPieChartParametersNode'
>;

type ScatterPlotParametersGQL = ExtractByTypename<
  AnalyticsVisualizationParameters,
  'AnalyticsVisualizationScatterPlotParametersNode'
>;

type TableParameters = ExtractByTypename<
  AnalyticsVisualizationParameters,
  'AnalyticsVisualizationTableParametersNode'
>;

export enum VisualizationType {
  BAR_CHART = 'BAR_CHART',
  HEATMAP = 'HEATMAP',
  LINE_CHART = 'LINE_CHART',
  PIE_CHART = 'PIE_CHART',
  SCATTER_PLOT = 'SCATTER_PLOT',
  TABLE = 'TABLE',
}

export type ScatterPlotParameters = Override<
  ScatterPlotParametersGQL,
  {regressionConfig: RegressionConfig | null}
>;

export interface VisualizationTypeToParameters {
  [VisualizationType.BAR_CHART]: BarChartParameters;
  [VisualizationType.HEATMAP]: HeatmapParameters;
  [VisualizationType.LINE_CHART]: LineChartParameters;
  [VisualizationType.SCATTER_PLOT]: ScatterPlotParameters;
  [VisualizationType.TABLE]: TableParameters;
  [VisualizationType.PIE_CHART]: PieChartParameters;
}

export interface XYChartParameters {
  xAxisColumnName: string;
  yAxisColumnName: string;
  sortDirection?: SortDirection | null;
}

export interface BaseChartParameters extends XYChartParameters {
  seriesColumnName: string | null;
  yAxisScale: AxisScale;
}

export interface Continuous2DChartParameters extends BaseChartParameters {
  xAxisScale: AxisScale;
  yAxisIncludesZero: boolean;
}

export interface HeatmapChartParameters extends XYChartParameters {
  metricColumnName: string;
  gradientDirection: GradientDirection;
}

export interface OutlierSettingChartParameters {
  outlierConfig: OutlierConfig | null;
}
export interface RegressionTypeChartParameters {
  regressionConfig: RegressionConfig | null;
}

export type ParametersOverride = Partial<
  BarChartParameters | HeatmapParameters | LineChartParameters | ScatterPlotParameters | TableParameters
>;

export function isRegressionTypeChartParameters(
  chartParameters: ParametersOverride
): chartParameters is RegressionTypeChartParameters {
  return chartParameters.hasOwnProperty('regressionConfig');
}

export function isXYChartParameters(
  chartParameters: ParametersOverride
): chartParameters is XYChartParameters {
  return (
    chartParameters.hasOwnProperty('xAxisColumnName') && chartParameters.hasOwnProperty('yAxisColumnName')
  );
}
